import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { routingConstants } from '../../_constants/routing-constants.const';
import { GenericModalUtil } from '../../_helpers/generic-modal.util';
import { AccessRole, AccessService, AccessUser } from '../../_services/access.service';
import { UserFormDialog, UserFormDialogData, UserFormMode } from '../user-form-dialog/user-form-dialog';

// Same size for add, edit and approve, regardless of content.
const FORM_DIALOG_WIDTH = '520px';
const FORM_DIALOG_MAX_WIDTH = '90vw';

// /manager: a manager adds users, sets their role and handles access requests.
@Component({
  selector: 'app-user-management',
  imports: [TranslateModule, MatButton, MatTabsModule, DatePipe],
  templateUrl: './user-management.html',
  styleUrl: './user-management.scss',
})
export class UserManagement implements OnInit {
  private readonly accessService = inject(AccessService);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  protected readonly users = signal<AccessUser[]>([]);
  protected readonly roles = signal<AccessRole[]>([]);
  protected readonly multipleRoles = signal(false);
  protected readonly loading = signal(true);
  protected readonly loadError = signal('');
  protected readonly searchTerm = signal('');

  protected readonly activeUsers = computed(() => this.users().filter((u) => u.status === 'Actief'));
  protected readonly requests = computed(() => this.users().filter((u) => u.status === 'Aangevraagd'));
  protected readonly filteredUsers = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    return this.activeUsers().filter(
      (u) => !term || u.email.includes(term) || (u.displayName ?? '').toLowerCase().includes(term),
    );
  });

  public ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadError.set('');
    forkJoin([this.accessService.getUsers(), this.accessService.getRoles()]).subscribe({
      next: ([users, catalog]) => {
        this.users.set(users);
        this.roles.set(catalog.roles);
        this.multipleRoles.set(catalog.multipleRoles);
        this.loading.set(false);
      },
      error: (error) => {
        this.loadError.set(AccessService.errorMessage(error));
        this.loading.set(false);
      },
    });
  }

  protected roleName(code: string): string {
    return this.roles().find((r) => r.code === code)?.name ?? code;
  }

  protected onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  protected openForm(mode: UserFormMode, user: AccessUser | null = null): void {
    const data: UserFormDialogData = { mode, user, roles: this.roles(), multipleRoles: this.multipleRoles() };
    this.dialog
      .open<UserFormDialog, UserFormDialogData, AccessUser>(UserFormDialog, {
        data,
        autoFocus: false,
        width: FORM_DIALOG_WIDTH,
        maxWidth: FORM_DIALOG_MAX_WIDTH,
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.users.update((users) =>
            [...users.filter((u) => u.id !== saved.id), saved].sort((a, b) => a.email.localeCompare(b.email)),
          );
        }
      });
  }

  protected remove(user: AccessUser): void {
    const message = user.status === 'Aangevraagd' ? 'access.manager.confirmReject' : 'access.manager.confirmDelete';
    GenericModalUtil.openConfirmActionDialog(this.translateService, this.dialog, message)
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.accessService.deleteUser(user.id).subscribe({
          next: () => this.users.update((users) => users.filter((u) => u.id !== user.id)),
          error: (error) => this.loadError.set(AccessService.errorMessage(error)),
        });
      });
  }

  protected back(): void {
    this.router.navigate([routingConstants.transcriber]);
  }
}
